// =====================================================================
// src/pages/intl-acca/IntlAccaHomePageEditorial.tsx
// =====================================================================
// Direction B of the Home surface — editorial hero with paywalled
// leg teaser. Intended for non-subscriber and trial viewers (the
// conversion path). Direction A (IntlAccaHomePage.tsx) is for paid
// viewers (terminal-dense feed).
//
// Routes: rendered conditionally inside the IntlAccaHomeRoute wrapper
// based on tier — host picks A or B before render.

import type {
  IntlAccaTournament,
  IntlAccaSlipWithLegs,
  IntlAccaSlipLeg,
  IntlAccaPassPurchase,
} from "../../db/intl-acca-types";
import { formatClvPp, formatOdds, legStage } from "../../db/intl-acca-types";
import { TournamentStateBanner } from "../../components/intl-acca/TournamentStateBanner";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { PassPurchaseCard } from "../../components/intl-acca/PassPurchaseCard";
import { CLVConfidencePill } from "../../components/intl-acca/CLVConfidencePill";
import type { IntlTrialPhase } from "../../intl-acca/useIntlAccaHome.ts";

export interface IntlAccaHomePageEditorialProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  /** The Focus slip — fully unlocked during trial, teased otherwise. */
  focusSlip: IntlAccaSlipWithLegs | null;
  existingPass: IntlAccaPassPurchase | null;
  matchLabels?: Record<string, string>;
  /** PRD §4 Stream 2 funnel phase. Drives lock/footer/CTA. */
  phase: IntlTrialPhase;
  /** Trial day index (1-3). Only set when phase === 'trial'. */
  trialDay: number | null;
  onPurchasePass?: () => void;
  /** "Unlock" CTA on locked legs. */
  onUpgrade?: () => void;
}

export function IntlAccaHomePageEditorial(
  props: IntlAccaHomePageEditorialProps,
): JSX.Element {
  const {
    loading, error, tournament, focusSlip, existingPass, matchLabels,
    phase, trialDay, onPurchasePass, onUpgrade,
  } = props;

  // Per-leg lock by funnel phase (PRD §4 Stream 2):
  //   trial   → full Focus Acca unlocked (Value/Power are Pass-only,
  //             not shown on this surface at all).
  //   teaser  → free desire-teaser: leg 1 visible, rest locked (§3.2).
  //   expired → trial spent, HARD paywall: every leg locked, no grace.
  //   subscriber never reaches this surface (Direction A).
  const legLocked = (i: number): boolean =>
    phase === "trial" ? false : phase === "teaser" ? i > 0 : true;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading…</div>
      </div>
    );
  }
  if (error || !tournament) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Could not load."
          body={error ?? "Tournament not found."}
        />
      </div>
    );
  }

  return (
    <div className="ia-root">
      <TournamentStateBanner tournament={tournament} />

      {/* HERO */}
      <section
        style={{
          padding: "64px var(--sz-space-7)",
          borderBottom: "1px solid var(--border-faint)",
          display: "grid",
          gridTemplateColumns: "1.3fr 1fr",
          gap: 64,
          alignItems: "center",
        }}
      >
        <div>
          <div className="ia-eyebrow">
            {tournament.name} · {tournament.region}
            {phase === "trial" && trialDay
              ? ` · Trial · Day ${trialDay}/3`
              : phase === "expired"
                ? " · Trial ended"
                : ""}
          </div>
          <h1
            style={{
              fontFamily: "var(--sz-font-head)",
              fontSize: "var(--sz-head-xl)",
              fontWeight: 500,
              lineHeight: 0.98,
              marginTop: 22,
              letterSpacing: "-0.015em",
            }}
          >
            The tournament is{" "}
            <em style={{ color: "var(--accent)", fontStyle: "italic" }}>priced.</em>
            <br />
            You'll see exactly{" "}
            <span style={{ fontStyle: "italic" }}>how.</span>
          </h1>
          <p
            className="ia-sub"
            style={{ marginTop: 28, fontSize: "var(--sz-body-lg)", maxWidth: 580 }}
          >
            For 21 days, STRIKZA runs a dedicated pricing engine on every
            fixture — squad freshness, dead rubbers, host advantage,
            referee DNA, sharp money. The output is a reasoned Slip per
            matchday. Not tips. Probability.
          </p>
        </div>

        {/* Pass card pinned right */}
        <PassPurchaseCard
          tournament={tournament}
          existingPass={existingPass}
          onPurchase={onPurchasePass}
          primary
        />
      </section>

      {/* Slip teaser — first leg open, rest paywalled */}
      <section style={{ padding: "48px var(--sz-space-7) 0" }}>
        <div className="ia-eyebrow" style={{ marginBottom: 18 }}>
          Today's Focus Acca
        </div>

        {!focusSlip ? (
          <HonestEmptyState variant="no-signals" />
        ) : (
          <div className="ia-slip-card">
            <header
              style={{
                padding: "var(--sz-space-5) var(--sz-space-5) var(--sz-space-4)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                gap: 24,
                borderBottom: "1px solid var(--border-faint)",
              }}
            >
              <div>
                <span
                  className="ia-mono"
                  style={{
                    fontSize: "var(--sz-mono-sm)",
                    color: "var(--text-tertiary)",
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                  }}
                >
                  Three legs · three reasons
                </span>
                <h2
                  style={{
                    fontFamily: "var(--sz-font-head)",
                    fontSize: "var(--sz-head-md)",
                    fontWeight: 500,
                    marginTop: 12,
                    lineHeight: 1.05,
                    color: "var(--text)",
                  }}
                >
                  Combined{" "}
                  <em style={{ color: "var(--accent)", fontStyle: "italic" }}>
                    {formatOdds(focusSlip.total_odds)}×
                  </em>
                </h2>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="ia-metric-label">SLIP CLV</div>
                {focusSlip.clv_score_final == null ? (
                  <div className="ia-metric-value-empty" style={{ marginTop: 6 }}>
                    Awaits closes
                  </div>
                ) : (
                  <div
                    className="ia-mono"
                    style={{
                      fontSize: "var(--sz-head-sm)",
                      color: "var(--accent-text)",
                      marginTop: 4,
                    }}
                  >
                    {formatClvPp(focusSlip.clv_score_final)}
                  </div>
                )}
              </div>
            </header>

            {focusSlip.legs.map((leg, i) => (
              <EditorialLegRow
                key={leg.id}
                index={i + 1}
                leg={leg}
                locked={legLocked(i)}
                matchLabel={matchLabels?.[leg.match_id]}
                onUpgrade={onUpgrade}
              />
            ))}

            <footer
              style={{
                padding: "var(--sz-space-5) var(--sz-space-5)",
                background: "var(--bg-elev-2)",
                borderTop: "1px solid var(--sz-amber-dim)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 32,
              }}
            >
              <div>
                <div className="ia-eyebrow">
                  {phase === "trial"
                    ? "Focus Acca · full slip unlocked"
                    : phase === "expired"
                      ? "Trial ended · upgrade to continue"
                      : "Trial available · Focus Acca"}
                </div>
                <p
                  style={{
                    marginTop: 10,
                    fontFamily: "var(--sz-font-head)",
                    fontSize: "var(--sz-head-sm)",
                    color: "var(--text)",
                    maxWidth: 480,
                  }}
                >
                  {phase === "trial"
                    ? "Your 3-day trial unlocks the full Focus Acca. Value & Power Accas and the post-tournament CLV audit are Pass-only."
                    : phase === "expired"
                      ? "Your 3-day trial has ended. Unlock the Pass to continue — every Focus & Value leg, the reasoning, and the post-tournament CLV audit."
                      : "Free viewers see the opening leg only. Unlock the Pass to read every reasoning note, every edge, and the post-tournament CLV audit."}
                </p>
              </div>
              <button
                type="button"
                onClick={onPurchasePass}
                style={{
                  background: "var(--accent)",
                  color: "var(--sz-text-inverse)",
                  border: "none",
                  borderRadius: "var(--sz-radius-md)",
                  padding: "14px 24px",
                  fontFamily: "var(--sz-font-mono)",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                {phase === "expired"
                  ? "Upgrade to continue →"
                  : `Unlock ${tournament.name} →`}
              </button>
            </footer>
          </div>
        )}
      </section>

      <div style={{ height: 64 }} />
    </div>
  );
}

function EditorialLegRow({
  index, leg, locked, matchLabel, onUpgrade,
}: {
  index: number;
  leg: IntlAccaSlipLeg;
  locked: boolean;
  matchLabel?: string;
  onUpgrade?: () => void;
}): JSX.Element {
  const stage = legStage(leg);
  return (
    <div
      style={{
        padding: "var(--sz-space-5) var(--sz-space-5)",
        borderTop: "1px solid var(--border-faint)",
        display: "grid",
        gridTemplateColumns: "60px 1fr 200px",
        gap: 32,
        alignItems: "flex-start",
      }}
    >
      <div>
        <div
          style={{
            fontFamily: "var(--sz-font-display)",
            fontSize: 44,
            color: "var(--accent)",
            lineHeight: 1,
          }}
        >
          0{index}
        </div>
        <div className="ia-metric-label" style={{ marginTop: 4 }}>LEG</div>
      </div>

      <div>
        <div
          className="ia-mono"
          style={{
            fontSize: "var(--sz-mono-xs)",
            color: "var(--text-tertiary)",
            letterSpacing: "0.16em",
          }}
        >
          {matchLabel ?? leg.match_id}
        </div>
        <div
          style={{
            fontFamily: "var(--sz-font-head)",
            fontSize: "var(--sz-head-sm)",
            fontWeight: 500,
            marginTop: 8,
            color: "var(--text)",
          }}
        >
          <span style={{ color: "var(--accent)" }}>{leg.selection}</span>
        </div>

        {locked ? (
          <div
            style={{
              marginTop: 14,
              padding: "var(--sz-space-3) var(--sz-space-4)",
              background: "var(--bg-elev-2)",
              border: "1px solid var(--border)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              borderRadius: "var(--sz-radius-md)",
            }}
          >
            <div
              style={{
                fontSize: "var(--sz-body-sm)",
                color: "var(--text-quaternary)",
                filter: "blur(5px)",
                userSelect: "none",
                maxWidth: 480,
              }}
              aria-hidden
            >
              {leg.reasoning_note}
            </div>
            <button
              type="button"
              onClick={onUpgrade}
              className="ia-pill ia-pill-amber"
              style={{ cursor: "pointer", border: "none" }}
            >
              🔒 PASS REQUIRED
            </button>
          </div>
        ) : (
          /* PRD §12 — reasoning_note always visible on the open leg */
          <p
            style={{
              marginTop: 14,
              fontSize: "var(--sz-body-md)",
              color: "var(--text-secondary)",
              lineHeight: 1.55,
              maxWidth: 620,
            }}
          >
            {leg.reasoning_note}
          </p>
        )}
      </div>

      <div style={{ textAlign: "right" }}>
        <div className="ia-metric-label">STRIKZA / MARKET</div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 6,
          }}
        >
          <div
            style={{
              fontFamily: "var(--sz-font-display)",
              fontSize: 36,
              color: locked ? "var(--text-tertiary)" : "var(--text)",
              lineHeight: 1,
            }}
          >
            {locked ? "••" : formatOdds(leg.strikza_price)}
          </div>
          <div className="ia-mono" style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
            / {locked ? "••" : formatOdds(leg.market_price_at_selection)}
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <CLVConfidencePill
            confidence={leg.clv_confidence}
            clv_pp={locked ? null : leg.clv_pp}
            hideValue={locked}
          />
        </div>
        {stage === "unsettled" && !locked ? (
          <div className="ia-clv-cell-empty" style={{ marginTop: 6 }}>
            Awaiting close
          </div>
        ) : null}
      </div>
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// Same shape as IntlAccaHomePage; the IntlAccaHomeRoute wrapper picks
// between Direction A and Direction B based on tier:
//
//   const editorialVariant = tier === 'free' || tier === 'trial';
//   return editorialVariant
//     ? <IntlAccaHomePageEditorial {...editorialProps} />
//     : <IntlAccaHomePage {...termalProps} />;
